// Migrated from cherry-web/utils/simple-badge-viewer.ts.
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  DirectionalLight,
  Box3,
  Vector3,
  MeshStandardMaterial,
  Object3D,
  Euler,
} from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { Tween, Easing, Group as TweenGroup } from "@tweenjs/tween.js";
function createRenderLoop(render) {
  let animationFrameId = null;
  function start() {
    if (animationFrameId) return;
    const animate = () => {
      render();
      animationFrameId = requestAnimationFrame(animate);
    };
    animate();
  }
  function stop() {
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
  }
  return {
    start,
    stop,
  };
}
function createSceneManager(scene) {
  const models = [];
  function addModel(model) {
    scene.add(model);
    models.push(model);
    return model;
  }
  function clear() {
    models.forEach((model) => {
      scene.remove(model);
    });
    models.length = 0;
  }
  return {
    addModel,
    clear,
  };
}
function createRotationController(targetObject, containerElement) {
  const state = {
    isRotating: false,
    startX: 0,
    currentRotation: 0,
    targetRotation: 0,
    rotationSpeed: 0.01,
    dampingFactor: 0.08,
  };
  // 缓存动画实例
  let rotationTween = null;
  // 创建动画组
  const tweenGroup = new TweenGroup();
  // 触摸事件处理
  function handleTouchStart(event) {
    if (!targetObject || !event.touches.length) return;
    event.preventDefault();
    event.stopPropagation();
    // 如果有正在进行的动画，停止它
    if (rotationTween) {
      rotationTween.stop();
      rotationTween = null;
    }
    state.isRotating = true;
    state.startX = event.touches[0].clientX;
    state.currentRotation = targetObject.rotation.y;
    state.targetRotation = targetObject.rotation.y;
  }
  function handleTouchMove(event) {
    if (!state.isRotating || !targetObject || !event.touches.length) return;
    event.preventDefault();
    event.stopPropagation();
    // 计算水平移动距离并转换为旋转角度
    const deltaX = event.touches[0].clientX - state.startX;
    state.targetRotation = state.currentRotation + deltaX * state.rotationSpeed;
    targetObject.rotation.y = state.targetRotation;
  }
  function handleTouchEnd(event) {
    event.preventDefault();
    event.stopPropagation();
    state.isRotating = false;
  }
  // 鼠标事件处理
  function handleMouseDown(event) {
    if (!targetObject) return;
    // 如果有正在进行的动画，停止它
    if (rotationTween) {
      rotationTween.stop();
      rotationTween = null;
    }
    state.isRotating = true;
    state.startX = event.clientX;
    state.currentRotation = targetObject.rotation.y;
    state.targetRotation = targetObject.rotation.y;
  }
  function handleMouseMove(event) {
    if (!state.isRotating || !targetObject) return;
    const deltaX = event.clientX - state.startX;
    state.targetRotation = state.currentRotation + deltaX * state.rotationSpeed;
    targetObject.rotation.y = state.targetRotation;
  }
  function handleMouseUp() {
    state.isRotating = false;
  }
  // 绑定事件
  function bindEvents() {
    if (!containerElement) return;
    // 触摸事件
    containerElement.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    containerElement.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    containerElement.addEventListener("touchend", handleTouchEnd, {
      passive: false,
    });
    // 鼠标事件
    containerElement.addEventListener("mousedown", handleMouseDown);
    containerElement.addEventListener("mousemove", handleMouseMove);
    containerElement.addEventListener("mouseup", handleMouseUp);
    containerElement.addEventListener("mouseleave", handleMouseUp);
  }
  // 解绑事件
  function unbindEvents() {
    if (!containerElement) return;
    containerElement.removeEventListener("touchstart", handleTouchStart);
    containerElement.removeEventListener("touchmove", handleTouchMove);
    containerElement.removeEventListener("touchend", handleTouchEnd);
    containerElement.removeEventListener("mousedown", handleMouseDown);
    containerElement.removeEventListener("mousemove", handleMouseMove);
    containerElement.removeEventListener("mouseup", handleMouseUp);
    containerElement.removeEventListener("mouseleave", handleMouseUp);
  }
  // 使用TWEEN.js启动初始旋转动画
  function startInitialAnimation() {
    if (!targetObject) return;
    // 确保没有正在进行的动画
    if (rotationTween) {
      rotationTween.stop();
      rotationTween = null;
    }
    // 初始旋转位置
    const startRotation = { y: targetObject.rotation.y };
    // 目标位置：旋转两圈
    const targetRotation = { y: targetObject.rotation.y + Math.PI * 4 };
    // 创建动画 - 在v25版本中，使用正确的参数
    rotationTween = new Tween(startRotation)
      .to(targetRotation, 1500) // 动画持续1.5秒
      .easing(Easing.Quadratic.InOut) // 使用二次缓入缓出
      .onUpdate(() => {
        if (targetObject) {
          targetObject.rotation.y = startRotation.y;
        }
      })
      .onComplete(() => {
        if (targetObject) {
          state.targetRotation = targetObject.rotation.y;
        }
        rotationTween = null;
      });
    // 添加到动画组并启动
    tweenGroup.add(rotationTween);
    rotationTween.start();
  }
  // 更新动画
  function update(time) {
    // 更新动画组
    tweenGroup.update(time);
  }
  return {
    bindEvents,
    unbindEvents,
    startInitialAnimation,
    update,
  };
}
export const createSimpleBadgeViewer = (modelBuffer) => {
  let scene = null;
  let camera = null;
  let renderer = null;
  let container = null;
  let rotationController = null;
  let sceneManager = null;
  let loadedModel = null;
  let disposed = false;
  const replacedMaterials = new Set();
  const releaseModel = (model) => {
    const textures = new Set();
    model.traverse((child) => {
      child.geometry?.dispose();
      const materials = Array.isArray(child.material)
        ? child.material
        : [child.material];
      materials.filter(Boolean).forEach((material) => {
        Object.values(material).forEach((value) => {
          if (value?.isTexture) textures.add(value);
        });
        material.dispose();
      });
    });
    textures.forEach((texture) => texture.dispose());
  };
  const resize = () => {
    if (disposed || !container || !camera || !renderer) return;
    const width = container.clientWidth || 1;
    const height = container.clientHeight || 300;
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height);
  };
  const initBasicScene = (domElement) => {
    container = domElement;
    const width = container.clientWidth;
    const height = container.clientHeight || 300;
    // scene
    scene = new Scene();
    scene.background = null;
    // camera
    camera = new PerspectiveCamera(50, width / height, 0.01, 1000);
    camera.position.set(0, 0, 40);
    camera.lookAt(0, 0, 0);
    camera.up.set(0, 1, 0);
    // render
    renderer = new WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
      precision: "highp",
    });
    renderer.setClearColor(0x000000, 0); // 设置透明背景
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, height);
    container.innerHTML = "";
    container.appendChild(renderer.domElement);
    window.addEventListener("resize", resize);
    // scene manager
    if (scene) {
      sceneManager = createSceneManager(scene);
    }
    // light
    if (scene) {
      // 环境光
      // const ambientLight = new AmbientLight(0xffffff, 1.0)
      // scene.add(ambientLight)
      const leftLight = new DirectionalLight(0xffffff, 1.0);
      leftLight.position.set(-20, 12, 40);
      scene.add(leftLight);
      const rightLight = new DirectionalLight(0xffffff, 1.0);
      rightLight.position.set(20, 12, 40);
      rightLight.intensity = 2;
      rightLight.castShadow = true;
      rightLight.lookAt(0, -12, 0);
      scene.add(rightLight);
      const topLight = new DirectionalLight(0xffffff, 1.0);
      topLight.position.set(0, -22, 30);
      topLight.intensity = 2;
      rightLight.lookAt(0, -12, 0);
      scene.add(topLight);
      // 背面灯光
      const backLeftLight = new DirectionalLight(0xffffff, 1.0);
      backLeftLight.position.set(-20, 12, -40);
      scene.add(backLeftLight);
      const backRightLight = new DirectionalLight(0xffffff, 1.0);
      backRightLight.position.set(20, 12, -40);
      backRightLight.intensity = 2;
      backRightLight.castShadow = true;
      backRightLight.lookAt(0, -12, 0);
      scene.add(backRightLight);
      const backTopLight = new DirectionalLight(0xffffff, 1.0);
      backTopLight.position.set(0, -22, -30);
      backTopLight.intensity = 2;
      backTopLight.lookAt(0, -12, 0);
      scene.add(backTopLight);
    }
    // ThreeJS Devtools 支持
    if (typeof window !== "undefined" && window.__THREE_DEVTOOLS__) {
      window.__THREE_DEVTOOLS__.dispatchEvent(
        new CustomEvent("observe", { detail: scene })
      );
      if (renderer) {
        window.__THREE_DEVTOOLS__.dispatchEvent(
          new CustomEvent("observe", { detail: renderer })
        );
      }
    }
  };
  const loadModel = async (
    onProgress = () => undefined,
    onError = () => undefined
  ) => {
    try {
      if (!scene || !container || !sceneManager) {
        onError();
        return Promise.reject(new Error("场景未初始化"));
      }
      const loader = new GLTFLoader();
      const gltf = await loader.parseAsync(modelBuffer, "");
      const model = gltf.scene;
      if (disposed) {
        releaseModel(model);
        return;
      }
      // 遍历模型的所有子对象，应用金属材质
      model.traverse((child) => {
        if (child instanceof Object3D && "material" in child) {
          if (["本体", "厚度", "水印"].includes(child.name)) {
            const materials = Array.isArray(child.material)
              ? child.material
              : [child.material];
            materials.forEach((material) => replacedMaterials.add(material));
          }
          if (child.name === "本体") {
            child.material = new MeshStandardMaterial({
              ...child.material,
              color: 15592941,
              roughness: 0.4,
              metalness: 1,
              emissive: 16777215,
              emissiveIntensity: 0,
              envMapRotation: new Euler(0, 0, 0),
              envMapIntensity: 1,
              blendColor: 0,
            });
          }
          if (child.name === "厚度") {
            child.material = new MeshStandardMaterial({
              ...child.material,
              color: 16777215,
              roughness: 0.3,
              metalness: 1,
              emissive: 0,
              envMapRotation: new Euler(0, 0, 0),
              envMapIntensity: 1,
              blendColor: 0,
            });
          }
          if (child.name === "水印") {
            child.material = new MeshStandardMaterial({
              ...child.material,
              color: 9539985,
              roughness: 0.03,
              metalness: 1,
              emissive: 16777215,
              emissiveIntensity: 0.2,
              envMapRotation: new Euler(0, 0, 0),
              envMapIntensity: 1,
              blendColor: 0,
            });
          }
        }
      });
      const box = new Box3().setFromObject(model);
      // center
      const center = box.getCenter(new Vector3());
      model.position.set(-center.x, -center.y, -center.z);
      loadedModel = sceneManager.addModel(model);
      // 初始化旋转控制器
      if (container && loadedModel) {
        rotationController = createRotationController(loadedModel, container);
        rotationController.bindEvents();
        // 启动初始旋转动画
        if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          rotationController.startInitialAnimation();
        }
      }
      onProgress(100);
    } catch (error) {
      if (!disposed) onError();
    }
  };
  const render = (time) => {
    if (!renderer || !scene || !camera) return;
    if (rotationController) {
      rotationController.update(time);
    }
    renderer.render(scene, camera);
  };
  const { start: startRenderLoop, stop: stopRenderLoop } =
    createRenderLoop(render);
  const init = async (
    domElement,
    onProgress = () => undefined,
    onError = () => undefined
  ) => {
    try {
      if (disposed) return;
      initBasicScene(domElement);
      await loadModel(onProgress, onError);
      if (!disposed && loadedModel) startRenderLoop();
    } catch (error) {
      if (!disposed) onError();
    }
  };
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    stopRenderLoop();
    window.removeEventListener("resize", resize);
    if (loadedModel) releaseModel(loadedModel);
    replacedMaterials.forEach((material) => material?.dispose());
    replacedMaterials.clear();
    if (rotationController) {
      rotationController.unbindEvents();
    }
    if (sceneManager) {
      sceneManager.clear();
    }
    if (renderer) {
      renderer.dispose();
      renderer.domElement.remove();
    }
  };
  return {
    init,
    dispose,
  };
};
